/**
 * Keeping a voice call alive when the person locks the phone, switches app or
 * loses signal: what to do about a call that should be live, and the browser
 * features that help (Media Session for the lock screen and headset buttons,
 * Wake Lock for the screen). Kept free of React and of the app's path aliases
 * so it can be tested on its own; the browser objects are passed in.
 *
 * What the web allows, and does not:
 * - Android Chrome can keep a call's audio and microphone going with the tab
 *   in the background or the screen off (it shows the tab is using the
 *   microphone), but Android may still suspend Chrome, which drops the call.
 *   It reconnects when the page runs again.
 * - iOS Safari stops the microphone when the screen locks or Safari leaves the
 *   foreground, and suspends the page. Nothing a web page does keeps
 *   listening; the call reconnects when the person comes back.
 * - Wake Lock only stops the screen dimming while Helm is on screen. It cannot
 *   stop someone locking the phone.
 */

export type PeerState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed'
export type ChannelState = 'connecting' | 'open' | 'closing' | 'closed'

/** A call that should be live, as the page can see it. */
export interface CallHealth {
  /** Whether the page is on screen. */
  visible: boolean
  peer: PeerState
  channel: ChannelState
  /** The microphone track; iOS ends it when the phone locks. */
  microphone: 'live' | 'ended'
  /** Reconnects tried since the call was last healthy. */
  attempts: number
}

export type CallAction =
  /** Nothing to do. */
  | 'keep'
  /** Open a new connection on the same conversation, without greeting again. */
  | 'reconnect'
  /** Broken, but the page is hidden: a hidden page may not get the microphone. Try on return. */
  | 'wait'
  /** Tried enough; end the call and say so. */
  | 'give-up'

export const MAX_RECONNECTS = 3

/** Whether a connection that should be up is broken beyond waiting for. */
export function isBroken(health: Pick<CallHealth, 'peer' | 'channel' | 'microphone'>): boolean {
  return (
    health.peer === 'failed' ||
    health.peer === 'closed' ||
    health.peer === 'disconnected' ||
    health.channel === 'closing' ||
    health.channel === 'closed' ||
    health.microphone === 'ended'
  )
}

/**
 * What to do about a call. Asked when the connection changes, when the
 * microphone stops, and when the page comes back on screen or online. A
 * 'disconnected' peer is asked about again after a grace period, since ICE
 * often recovers from a brief drop on its own.
 */
export function nextCallAction(health: CallHealth): CallAction {
  if (!isBroken(health)) return 'keep'
  if (health.attempts >= MAX_RECONNECTS) return 'give-up'
  if (!health.visible) return 'wait'
  return 'reconnect'
}

/** How long to wait before reconnect attempt `attempt` (0-based): at once, then backing off. */
export function reconnectDelay(attempt: number): number {
  return [0, 1_000, 3_000][attempt] ?? 3_000
}

/** How long a 'disconnected' peer gets to recover before the call reconnects. */
export const DISCONNECT_GRACE_MS = 4_000

type ActionName = 'play' | 'pause' | 'stop' | 'hangup' | 'togglemicrophone'

/** The slice of `navigator.mediaSession` used here; missing where unsupported. */
export interface MediaSessionLike {
  metadata: unknown
  playbackState?: string
  setActionHandler(action: ActionName, handler: (() => void) | null): void
  setMicrophoneActive?: (active: boolean) => void | Promise<void>
}

export interface CallControls {
  hangUp: () => void
  setMuted: (muted: boolean) => void
  toggleMute: () => void
}

/**
 * Puts the call on the lock screen, the notification shade and headset
 * buttons where the browser offers them: "Beacon" as what is playing, pause to
 * mute, play to unmute, and hang up. Each action is tried on its own, since
 * browsers reject the ones they do not know. Returns `update` for status
 * changes and `release` for the end of the call.
 */
export function claimMediaSession(
  session: MediaSessionLike | undefined,
  controls: CallControls,
  makeMetadata: ((init: Record<string, unknown>) => unknown) | undefined,
) {
  if (!session) return { update: () => undefined, release: () => undefined }
  const handlers: Array<[ActionName, () => void]> = [
    ['pause', () => controls.setMuted(true)],
    ['play', () => controls.setMuted(false)],
    ['togglemicrophone', controls.toggleMute],
    ['stop', controls.hangUp],
    ['hangup', controls.hangUp],
  ]
  for (const [action, handler] of handlers) {
    try {
      session.setActionHandler(action, handler)
    } catch {
      // Not supported here.
    }
  }
  const update = ({ status, muted }: { status: string; muted: boolean }) => {
    try {
      session.metadata = makeMetadata?.({
        title: 'Beacon',
        artist: muted ? 'Muted' : status,
        album: 'Helm',
        artwork: [
          { src: '/icon-192.png', sizes: '192x192', type: 'image/png' },
          { src: '/icon-512.png', sizes: '512x512', type: 'image/png' },
        ],
      })
      session.playbackState = muted ? 'paused' : 'playing'
      void session.setMicrophoneActive?.(!muted)
    } catch {
      // Not supported here.
    }
  }
  const release = () => {
    for (const [action] of handlers) {
      try {
        session.setActionHandler(action, null)
      } catch {
        // Not supported here.
      }
    }
    try {
      session.metadata = null
      session.playbackState = 'none'
    } catch {
      // Not supported here.
    }
  }
  return { update, release }
}

/** The slice of Wake Lock used here. */
export interface WakeLockLike {
  request(type: 'screen'): Promise<{ release(): Promise<void> }>
}

export interface PageVisibility {
  visibilityState: string
  addEventListener(type: 'visibilitychange', listener: () => void): void
  removeEventListener(type: 'visibilitychange', listener: () => void): void
}

/**
 * Keeps the screen on while a call is live and Helm is on screen, so the
 * phone does not lock itself mid-sentence (on iOS that would stop the
 * microphone). The browser drops the lock whenever the page is hidden; it is
 * taken again when the page comes back.
 */
export function holdScreenAwake(wakeLock: WakeLockLike | undefined, page: PageVisibility) {
  if (!wakeLock) return { release: () => undefined }
  let lock: { release(): Promise<void> } | null = null
  let released = false
  const take = () => {
    if (released || lock || page.visibilityState !== 'visible') return
    wakeLock
      .request('screen')
      .then((granted) => {
        if (released) void granted.release().catch(() => undefined)
        else lock = granted
      })
      .catch(() => undefined)
  }
  const onVisibility = () => {
    // The browser already released it when the page was hidden.
    if (page.visibilityState !== 'visible') lock = null
    else take()
  }
  page.addEventListener('visibilitychange', onVisibility)
  take()
  return {
    release: () => {
      released = true
      page.removeEventListener('visibilitychange', onVisibility)
      void lock?.release().catch(() => undefined)
      lock = null
    },
  }
}
